import { Box, Button } from '@mui/material';
import { Download } from 'lucide-react';
import { LabeledValue, LabelIdValue } from '~/components/Fields/LabeledValue';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useDose from '~/hooks/patient/dose/useDose';
import {
    createCSVContent,
    createJSONContent,
    downloadCreatedFile,
} from '~/utils/io/files/handleFileExport';
import {
    buildOptimizationObjectivesCsv,
    buildOptimizationObjectivesFilename,
    buildOptimizationObjectivesJson,
} from '~/utils/io/files/optimizationObjectives';
import DoseTargets from './DoseTargets';

export default function DoseExportDetails({ dose }: { dose: DoseMatrix }) {
    const { dose: doseState } = useDose(dose);
    const objectives = dose.optimizationObjectives;
    const hasObjectives = (objectives?.length ?? 0) > 0;

    const handleDownloadObjectivesJson = () => {
        const json = buildOptimizationObjectivesJson(objectives!);
        const jsonUri = createJSONContent(json);
        downloadCreatedFile(
            jsonUri,
            buildOptimizationObjectivesFilename(dose.plan?.label, dose.dicomUid)
        );
    };

    const handleDownloadObjectivesCsv = () => {
        const csv = buildOptimizationObjectivesCsv(objectives!);
        const csvUri = createCSVContent('', [csv]);
        downloadCreatedFile(
            csvUri,
            buildOptimizationObjectivesFilename(
                dose.plan?.label,
                dose.dicomUid,
                'csv'
            )
        );
    };

    return (
        <Box py={1} display="grid" gap={1}>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr">
                <LabeledValue label="Name" value={dose.plan?.label ?? 'Dose'} />
                <LabeledValue
                    label="Summation type"
                    value={dose.doseSummationType}
                />
                <LabelIdValue label="Series Instance UID" id={dose.dicomUid} />
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr">
                <LabeledValue label="Dose type" value={dose.doseType} />
                <LabeledValue
                    label="Dose max"
                    value={dose.doseMaxGy.toFixed(1) + dose.doseUnits}
                />
                <LabeledValue
                    label="Prediction Model"
                    value={dose?.generationModel?.label}
                />
            </Box>
            {doseState?.targets && (
                <LabeledValue
                    label="Targets"
                    value={
                        <DoseTargets
                            units={dose.doseUnits}
                            targets={doseState.targets.filter(
                                (t) => t.isTarget
                            )}
                        />
                    }
                />
            )}
            {hasObjectives && (
                <Box display="flex" justifyContent="flex-start" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download size={12} />}
                        onClick={handleDownloadObjectivesJson}
                        sx={{ fontSize: 11, textTransform: 'none' }}
                    >
                        Download optimization objectives (JSON)
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download size={12} />}
                        onClick={handleDownloadObjectivesCsv}
                        sx={{ fontSize: 11, textTransform: 'none' }}
                    >
                        Download optimization objectives (CSV)
                    </Button>
                </Box>
            )}
        </Box>
    );
}
